import React from 'react'

import { SxProps, useTheme } from '@mui/material'

import type {
  TAnalysisCalculationRowDto,
  TAnalysisResultDto
} from '@homepal/api-contract'

import { useAnalysis, useReportMetric } from 'redux/hooks/Analyses'
import { useIsMobile } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import ContentBox from 'components_new/molecules/ContentBox'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

import LineChart from 'components_new/molecules/charts/LineChart'
import ValueText from 'components_new/molecules/ValueText'

import AnalysisCalculation from 'components_new/organisms/analyses/AnalysisCalculation'
import MetricTitle from 'components_new/organisms/MetricTitle'
import { getPrefixValue } from 'components_new/organisms/Widget/Types/utils'

import { AUTO_SIGNIFICANT_FIGURES, getAutoScale } from 'utils/autoFormat'
import { getParsedDate } from 'utils/dateParser'

interface AnalysisTrendProps {
  calculation: TAnalysisCalculationRowDto[]
  comparativeValues: (number | null)[]
  id: string

  /** Set for one property; the selection is described by its counts. */
  orgUnit?: string
  potential: number
  qualifiedCount?: number
  result: TAnalysisResultDto | null
  segments: string[]
  selectedCount?: number
  startValue: number | null
  sx?: SxProps
  targetValue: number | null
  values: (number | null)[]
}

const AnalysisTrend = (props: AnalysisTrendProps) => {
  const {
    calculation,
    comparativeValues,
    id,
    orgUnit,
    potential,
    qualifiedCount,
    result,
    segments,
    selectedCount,
    startValue,
    sx,
    targetValue,
    values
  } = props

  const isMobile = useIsMobile()
  const theme = useTheme()
  const { lang, t } = useTranslation()

  const { analysis } = useAnalysis(id)
  const metric = useReportMetric(analysis)

  if (!analysis || !analysis.metric) return null

  const unit = metric?.unit || null

  // one unit for the chart, its lines and the improvement, e.g. mkr
  const { unitPrefix, unitLabel } = getAutoScale(
    [...values, ...comparativeValues, startValue, targetValue],
    unit,
    metric?.unit_prefixes
  )
  const scale = (value: number | null) => getPrefixValue(value, unitPrefix)

  const title = (
    <MetricTitle
      id={analysis.metric.id}
      link={true}
      settings={analysis.metric.settings}
      size={isMobile ? 'small' : 'medium'}
      sx={{ mb: 2 }}
    />
  )

  // Nothing qualified: there is no selection to sum up.
  if (qualifiedCount === 0) return <Box sx={sx}>{title}</Box>

  return (
    <Box sx={sx}>
      {title}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 2
        }}
      >
        {[
          {
            content: (
              <Text variant="h5">
                {orgUnit ?? (
                  <>
                    <Link
                      onClick={() =>
                        document
                          .getElementById('selection')
                          ?.scrollIntoView({ behavior: 'smooth' })
                      }
                    >
                      {selectedCount ?? '–'}
                    </Link>{' '}
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {`/ ${qualifiedCount ?? '–'} ${t(
                        'org_units',
                        'units',
                        true
                      )}`}
                    </Box>
                  </>
                )}
              </Text>
            ),
            title: t('analyses', 'report_selection_title')
          },
          {
            content: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ValueText
                  tooltip={t('analyses', 'report_start_value')}
                  unit={unitLabel}
                  value={scale(startValue)}
                  variant="h5"
                />
                <Icon color="disabled" name="ArrowForward" />
                <ValueText
                  tooltip={t('analyses', 'report_target_value')}
                  unit={unitLabel}
                  value={scale(targetValue)}
                  variant="h5"
                />
              </Box>
            ),
            title: t('analyses', 'report_improvement')
          },
          {
            content: (
              <ValueText
                color="success.text"
                displayPositiveIndicator={true}
                unit={'kr/år'}
                value={potential}
                variant="h5"
              />
            ),
            actions: (
              <AnalysisCalculation
                calculation={calculation}
                potential={potential}
                result={result}
              />
            ),
            title: t('analyses', 'report_potential_result')
          }
        ].map((item, i) => (
          <ContentBox
            border={true}
            header={{ actions: item.actions, title: item.title }}
            key={i}
            sx={{ flex: 1 }}
          >
            {item.content}
          </ContentBox>
        ))}
      </Box>

      <Box sx={{ height: 400 }}>
        <LineChart
          data={{
            datasets: [
              {
                color: theme.palette.brand.main,
                label: t('analyses', 'trend_current_period'),
                type: 'line',
                unit: unitLabel,
                values: values.map(scale)
              },
              {
                label: t('analyses', 'trend_previous_period'),
                type: 'line',
                unit: unitLabel,
                values: comparativeValues.map(scale)
              }
            ],
            horizontalLines: [
              ...(startValue !== null
                ? [
                    {
                      color: theme.palette.action.active,
                      label: t('analyses', 'report_start_value'),
                      value: scale(startValue) ?? startValue
                    }
                  ]
                : []),
              ...(targetValue !== null
                ? [
                    {
                      color: theme.palette.action.active,
                      label: t('analyses', 'report_target_value'),
                      value: scale(targetValue) ?? targetValue
                    }
                  ]
                : [])
            ],
            labels: segments.map((segment) =>
              getParsedDate(segment, lang, { year: 'short' })
            )
          }}
          significantFigures={AUTO_SIGNIFICANT_FIGURES}
          variant="detailed"
        />
      </Box>
    </Box>
  )
}

export default AnalysisTrend
